import { InstancedMesh, Vector3, Web3DViewer } from '@technology/web3d';
import {
  createClipStateTracker,
  type ClipStateTracker,
  ThreeJsManager,
  type PickResult,
} from '@trimble/web3d-utils';
import { StationInfo } from '@trimble/web3d-providers';
import { Accessor, createEffect, createMemo, on } from 'solid-js';
import {
  MAP_STATION_FAR_BOUNDARY,
  MAP_STATION_NEAR_BOUNDARY,
  MAP_STATION_PLANE_SIZE,
  DEFAULT_STATION_POINT_SIZE,
} from '../../capabilities/constants/map-station';
import {
  type StationData,
  type StationPoint,
  type PathSegment,
  type StationVisualConfig,
} from './station-visual-creator';
import { PathClipApplier } from './path-clip-applier';
import { type PathInstanceData } from './instanced-station-path-mesh';
import { StationPathPipeline } from './station-path-pipeline';
import { StationMarkerPipeline } from './station-marker-pipeline';

const DEFAULT_HEIGHT_OFFSET = 1.6;
const STATION_RENDER_ORDER = 2;

const DEFAULT_PATH_WIDTH_RATIO = 0.3;
const PATH_HEIGHT_OFFSET = 0.05;
const PATH_RENDER_ORDER = 1;

// Orchestration-level opacities; per-frame tween constants live in the pipelines.
const GLOBAL_OPACITY_MIN = 0.3;
const GLOBAL_OPACITY_MAX = 1.0;
const PATH_OPACITY_MIN = 0.3;
const PATH_OPACITY_MAX = 0.7;

const HOVER_SCALE_MULTIPLIER = 1.5;
// Far stations sit on a white path, so hover scaling would be visually noisy.
const HOVER_SCALE_MULTIPLIER_FAR = 1.0;

// ~20fps cap on LOD / distance work so heavy ticks never block animation frames.
const HEAVY_UPDATE_INTERVAL_MS = 50;

// Distance beyond which markers hide (still revealable via path hover).
const LOD_MARKER_DISTANCE_THRESHOLD = 300;

// Coalesces rapid run-loading bursts into a single path mesh rebuild.
const PATH_REBUILD_DEBOUNCE_MS = 500;

export interface StationVisualsConfig {
  /** Size of the plane geometry for station markers */
  stationPlaneSize?: number;
  /** Base point size for distance-based scaling */
  stationPointSize?: number;
  /** Path width as ratio of station plane size (0-1) */
  pathWidthRatio?: number;
  /** Near boundary distance for scaling */
  stationNearBoundary?: number;
  /** Far boundary distance for scaling */
  stationFarBoundary?: number;
  /** Height offset for markers below station Z */
  stationHeightOffset?: number;
  /** Distance threshold for LOD - markers hide beyond this distance unless path is hovered */
  lodMarkerDistanceThreshold?: number;
  /** Hover scale multiplier for near stations (within LOD distance) */
  hoverScaleMultiplier?: number;
  /** Hover scale multiplier for far stations (beyond LOD distance, shown on path hover) */
  hoverScaleMultiplierFar?: number;
  /** Use instanced mesh for path segments (better performance for large runs) */
  useInstancedPaths?: boolean;
}

/**
 * Read-only reactive view of capability state. Writes stay on the capability
 * so the public API surface remains stable for cross-pane sync coordinators
 * and the capability registry.
 */
export interface StationViewManagerDeps {
  readonly stationsInfo: Accessor<StationInfo[]>;
  readonly isEnabled: Accessor<boolean>;
  /** When active, unhovered markers dim so the selected one stands out. */
  readonly isActive: Accessor<boolean>;
  /** Pre-Z-resolved selected position; non-null triggers selection-dimming. */
  readonly currentStationPosition: Accessor<Vector3 | null>;
  /** Outer gate (e.g. cross-section disables markers). */
  readonly canShowStationMarkers?: Accessor<boolean>;
  /** Visual Z resolver (overridden by 2D map for a fixed height). */
  getStationZ(pos: Vector3): number;
  /**
   * Late-bound to the capability so subclass overrides of `handlePick` take
   * effect even though the manager is constructed before the subclass body.
   */
  onStationPicked(result: PickResult): void;
  readonly isStationMarkersVisible: Accessor<boolean>;
  readonly isNavigationPathVisible: Accessor<boolean>;
}

/**
 * @intent Orchestrates station-view rendering (markers, paths, LOD, hover,
 * animations, clipping) on behalf of {@link StationViewManagerDeps.isEnabled}'s
 * capability. Helper class, not a Capability/Coordinator — the capability
 * keeps navigation state, this owns Three.js resources via the two pipelines.
 */
export class StationViewManager {
  private readonly threeJsManager: ThreeJsManager;
  private readonly deps: StationViewManagerDeps;
  public readonly config: Required<StationVisualsConfig>;
  /** Reused across creator calls to avoid per-call allocation. */
  private readonly visualConfig: StationVisualConfig;

  /**
   * Single source of truth for station metadata. Pipelines read it via
   * deps callbacks; the marker pipeline mutates per-station LOD bookkeeping
   * during its heavy update, but creation/deletion is owned here.
   */
  protected stationDataMap: Map<string, StationData> = new Map();
  /** Index→key bridge rebuilt on path rebuild for instanced-mesh lookups. */
  private readonly stationIndexToKey: Map<number, string> = new Map();

  private readonly markerPipeline: StationMarkerPipeline;
  private readonly pathPipeline: StationPathPipeline;

  protected haveEventListeners = false;
  protected isUpdating = false;
  protected pendingUpdate: StationInfo[] | null = null;
  protected previousPositionKeys: Set<string> = new Set();
  private pathRebuildScheduledAt: number | null = null;
  private pathRebuildRafId: number | null = null;
  private latestStationsForPathRebuild: StationInfo[] | null = null;

  /** Serialises async create/dispose against incoming reactive updates. */
  private operationChain: Promise<void> = Promise.resolve();

  /** Trigger to recolour the path when marker visibility toggles. */
  private previousMarkersVisible = true;
  /** Trigger to reconcile near/far transitions on path re-show. */
  private previousPathsVisible = true;

  private lastHeavyUpdateTime = 0;

  /**
   * Markers/path segments are `ThreeJsManager` overlays drawn with
   * `depthTest: false`; without clip-state tracking they would float in
   * mid-air once the user clips into a building.
   */
  private readonly clipStateTracker: ClipStateTracker;
  private readonly pathClipApplier: PathClipApplier;

  /** Mirrors the prior capability's memo so the visuals effect fires identically. */
  protected readonly shouldShowMarkers: Accessor<boolean>;

  constructor(
    viewer: Web3DViewer,
    threeJsManager: ThreeJsManager,
    deps: StationViewManagerDeps,
    config?: StationVisualsConfig
  ) {
    this.threeJsManager = threeJsManager;
    this.deps = deps;

    this.clipStateTracker = createClipStateTracker(viewer);
    this.pathClipApplier = new PathClipApplier({
      tracker: this.clipStateTracker,
      getStationPosition: (index) =>
        this.getStationDataByIndex(index)?.position,
    });

    this.config = {
      stationPlaneSize: config?.stationPlaneSize ?? MAP_STATION_PLANE_SIZE,
      stationPointSize: config?.stationPointSize ?? DEFAULT_STATION_POINT_SIZE,
      pathWidthRatio: config?.pathWidthRatio ?? DEFAULT_PATH_WIDTH_RATIO,
      stationNearBoundary:
        config?.stationNearBoundary ?? MAP_STATION_NEAR_BOUNDARY,
      stationFarBoundary:
        config?.stationFarBoundary ?? MAP_STATION_FAR_BOUNDARY,
      stationHeightOffset: config?.stationHeightOffset ?? DEFAULT_HEIGHT_OFFSET,
      lodMarkerDistanceThreshold:
        config?.lodMarkerDistanceThreshold ?? LOD_MARKER_DISTANCE_THRESHOLD,
      hoverScaleMultiplier:
        config?.hoverScaleMultiplier ?? HOVER_SCALE_MULTIPLIER,
      hoverScaleMultiplierFar:
        config?.hoverScaleMultiplierFar ?? HOVER_SCALE_MULTIPLIER_FAR,
      useInstancedPaths: config?.useInstancedPaths ?? true,
    };

    this.visualConfig = {
      stationPlaneSize: this.config.stationPlaneSize,
      stationPointSize: this.config.stationPointSize,
      pathWidthRatio: this.config.pathWidthRatio,
      stationHeightOffset: this.config.stationHeightOffset,
      stationNearBoundary: this.config.stationNearBoundary,
      stationFarBoundary: this.config.stationFarBoundary,
      stationRenderOrder: STATION_RENDER_ORDER,
      pathRenderOrder: PATH_RENDER_ORDER,
      pathHeightOffset: PATH_HEIGHT_OFFSET,
    };

    this.shouldShowMarkers = createMemo(
      () =>
        this.deps.isEnabled() && (this.deps.canShowStationMarkers?.() ?? true)
    );

    this.markerPipeline = new StationMarkerPipeline(
      viewer,
      threeJsManager,
      {
        stationPointSize: this.config.stationPointSize,
        stationNearBoundary: this.config.stationNearBoundary,
        stationFarBoundary: this.config.stationFarBoundary,
        lodMarkerDistanceThreshold: this.config.lodMarkerDistanceThreshold,
        hoverScaleMultiplier: this.config.hoverScaleMultiplier,
        hoverScaleMultiplierFar: this.config.hoverScaleMultiplierFar,
      },
      this.visualConfig,
      {
        getAllStationData: () => this.stationDataMap.values(),
        getStationDataByIndex: (index) => this.getStationDataByIndex(index),
        getStationDataByKey: (key) => this.stationDataMap.get(key),
        getStationZ: (position) => this.deps.getStationZ(position),
        isStationMarkersVisible: () => this.deps.isStationMarkersVisible(),
        isPointInsideActiveClip: (position) =>
          this.clipStateTracker.isPointInsideActiveClip(position),
      }
    );

    this.pathPipeline = new StationPathPipeline(
      viewer,
      threeJsManager,
      this.pathClipApplier,
      {
        stationPlaneSize: this.config.stationPlaneSize,
        stationPointSize: this.config.stationPointSize,
        pathWidthRatio: this.config.pathWidthRatio,
        stationNearBoundary: this.config.stationNearBoundary,
        stationFarBoundary: this.config.stationFarBoundary,
        useInstancedPaths: this.config.useInstancedPaths,
      },
      this.visualConfig,
      {
        getStationDataByIndex: (index) => this.getStationDataByIndex(index),
        getStationZ: (position) => this.deps.getStationZ(position),
        isStationMarkersVisible: () => this.deps.isStationMarkersVisible(),
        isNavigationPathVisible: () => this.deps.isNavigationPathVisible(),
        getCurrentStationPosition: () => this.deps.currentStationPosition(),
      }
    );

    this.setupReactiveEffects();
  }

  // Public getters and pipeline pass-throughs below exist as test seams so
  // tests can assert visual state through the manager rather than through
  // bracket-access on private pipeline fields.

  public get markerPoints(): readonly StationPoint[] {
    return this.markerPipeline.markerPoints;
  }

  public get pathSegmentsList(): readonly PathSegment[] {
    return this.pathPipeline.segments;
  }

  public get instancedFarPathMesh(): InstancedMesh | null {
    return this.pathPipeline.instancedFarPathMesh;
  }

  public get instancedFarPathData(): readonly PathInstanceData[] {
    return this.pathPipeline.instancedFarPathData;
  }

  public get hoveredPoint(): StationPoint | null {
    return this.markerPipeline.hoveredPoint;
  }

  public calculatePathWidthForSegment(data: PathInstanceData): number {
    return this.pathPipeline.calculatePathWidthForSegment(data);
  }

  public hideInstancedPathSegment(instanceIndex: number): void {
    this.pathPipeline.hideInstancedSegment(instanceIndex);
  }

  public setHoveredMarker(point: StationPoint): void {
    this.markerPipeline.setHover(point);
  }

  private setupReactiveEffects(): void {
    createEffect(
      on(
        [
          this.shouldShowMarkers,
          this.deps.stationsInfo,
          this.deps.isStationMarkersVisible,
          this.deps.isNavigationPathVisible,
        ],
        ([shouldShowMarkers, stationsInfo, markersVisible, pathsVisible]) => {
          if (!shouldShowMarkers) {
            if (this.stationDataMap.size > 0) {
              this.operationChain = this.operationChain.then(async () => {
                await this.removeAllVisuals();
                await this.removeListeners();
              });
            }

            this.markerPipeline.clearHover();

            return;
          }

          this.operationChain = this.operationChain
            .then(() => this.updateVisuals(stationsInfo))
            .then(() => {
              this.markerPipeline.applyVisibility(markersVisible);
              this.pathPipeline.applyVisibility(pathsVisible);
            });
        }
      )
    );

    // Selection-driven dimming. Camera positioning stays on the capability.
    createEffect(() => {
      const position = this.deps.currentStationPosition();

      if (!position) {
        this.markerPipeline.restoreAllMarkersOpacity(GLOBAL_OPACITY_MAX);
        this.pathPipeline.setTargetOpacity(PATH_OPACITY_MAX);
        return;
      }

      this.markerPipeline.dimNonHoveredMarkers(GLOBAL_OPACITY_MIN);
      this.pathPipeline.setTargetOpacity(PATH_OPACITY_MIN);
    });

    createEffect(() => {
      const active = this.deps.isActive();

      const targetStationOpacity = active
        ? GLOBAL_OPACITY_MIN
        : GLOBAL_OPACITY_MAX;

      this.markerPipeline.setUnhoveredOpacity(targetStationOpacity);

      this.pathPipeline.setTargetOpacity(PATH_OPACITY_MAX);
      this.threeJsManager.forceRefresh();
    });

    // Force a heavy tick on clip changes so clipping takes effect immediately
    // rather than up to HEAVY_UPDATE_INTERVAL_MS later.
    createEffect(
      on(
        this.clipStateTracker.state,
        () => {
          this.lastHeavyUpdateTime = 0;
          this.threeJsManager.forceRefresh();
        },
        { defer: true }
      )
    );
  }

  /**
   * Dual-loop frame update: heavy work (LOD, distance) is throttled at
   * HEAVY_UPDATE_INTERVAL_MS so it never blocks the per-frame opacity /
   * scale / colour tweens.
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity -- SonarJS trial (NUMARK-1243): complexity 23 > 15 allowed; flagged for later refactor, not fixed here
  private readonly updateStationPoints = (): void => {
    if (!this.deps.isEnabled() || this.stationDataMap.size === 0) return;

    const now = performance.now();
    const shouldRunHeavyUpdate =
      now - this.lastHeavyUpdateTime > HEAVY_UPDATE_INTERVAL_MS;

    let needsRefresh = false;

    // Read accessors once: this loop is hot.
    const markersVisible = this.deps.isStationMarkersVisible();
    const pathsVisible = this.deps.isNavigationPathVisible();

    if (shouldRunHeavyUpdate) {
      this.lastHeavyUpdateTime = now;

      needsRefresh =
        this.markerPipeline.runHeavyUpdate(markersVisible) || needsRefresh;

      // Recolour the path on LOD transitions, marker toggles, or when paths
      // re-show (any LOD changes that happened while hidden need flushing).
      // `previousMarkersVisible` is only consumed when paths are visible so
      // pending recolours survive a hide/show cycle.
      const markersVisibilityChanged =
        markersVisible !== this.previousMarkersVisible;
      const pathsBecameVisible = pathsVisible && !this.previousPathsVisible;
      this.previousPathsVisible = pathsVisible;

      if (pathsVisible) {
        if (
          this.markerPipeline.hadLodTransition ||
          markersVisibilityChanged ||
          pathsBecameVisible
        ) {
          this.pathPipeline.updateTargetColors(markersVisible);
        }
        this.previousMarkersVisible = markersVisible;
      }

      if (pathsVisible) {
        // Clip first, then distance-scale: otherwise distance-scaling would
        // overwrite the zero-scale matrices the clip applier just wrote.
        needsRefresh = this.pathPipeline.applyClipping() || needsRefresh;

        if (
          this.config.useInstancedPaths &&
          this.pathPipeline.instancedFarPathMesh
        ) {
          needsRefresh =
            this.pathPipeline.updateInstancedPathScales(markersVisible) ||
            needsRefresh;
        }

        for (const segment of this.pathPipeline.segments) {
          needsRefresh =
            this.pathPipeline.updateSegmentScale(segment) || needsRefresh;
        }
      }
    }

    // Run marker tweens even when hidden so fade-outs complete.
    needsRefresh =
      this.markerPipeline.runLightAnimations(markersVisible) || needsRefresh;

    if (pathsVisible) {
      if (this.pathPipeline.hasActiveOpacityAnimation) {
        needsRefresh = this.pathPipeline.animateOpacity() || needsRefresh;
      }
      if (this.pathPipeline.hasActiveColorAnimation) {
        needsRefresh = this.pathPipeline.animateColors() || needsRefresh;
      }
    }

    if (needsRefresh) {
      this.threeJsManager.forceRefresh();
    }
  };

  /** Returns undefined for stale indices (e.g., during a path rebuild). */
  protected getStationDataByIndex(
    stationIndex: number
  ): StationData | undefined {
    const key = this.stationIndexToKey.get(stationIndex);
    return key ? this.stationDataMap.get(key) : undefined;
  }

  private readonly handlePickInternal = (result: PickResult): void => {
    this.deps.onStationPicked(result);
  };

  private readonly handleHover = (result: PickResult | null): void => {
    const currentHover = this.markerPipeline.hoveredPoint;

    if (result?.meshId === currentHover?.mesh.id) {
      return;
    }

    // Hidden marker meshes stay pickable. Resolving the hover would call
    // setHover and leave the marker visible until the hover ends.
    if (!result || !this.deps.isStationMarkersVisible()) {
      this.markerPipeline.resetHover();
      return;
    }

    const point = this.resolveHoverPoint(result);
    if (!point) {
      this.markerPipeline.resetHover();
      return;
    }

    // Same station resolved from a different mesh (e.g., near path vs marker):
    // skip the reset+reapply cycle to avoid flicker.
    if (point === currentHover) {
      return;
    }

    this.markerPipeline.resetHover();
    this.markerPipeline.setHover(point);
  };

  /**
   * Hover-only resolution. Direct marker hits are always honoured (the caller
   * has already confirmed markers are visible). Hidden path meshes are removed
   * from the raycaster; a path hit that still arrives is ignored so it cannot
   * create a floating marker.
   */
  private resolveHoverPoint(result: PickResult): StationPoint | null {
    const directMarker = this.markerPipeline.findByMeshId(result.meshId);
    if (directMarker) {
      return directMarker;
    }

    if (!this.deps.isNavigationPathVisible()) {
      return null;
    }

    return this.findStationPointForPickResult(result);
  }

  /**
   * Resolves any pickable station mesh (marker, instanced path, near path,
   * or non-instanced segment) to a `StationPoint`. For path picks the closest
   * station to the camera wins.
   */
  public findStationPointForPickResult(
    result: PickResult
  ): StationPoint | null {
    const { meshId, instanceId } = result;

    const point = this.markerPipeline.findByMeshId(meshId);
    if (point) {
      return point;
    }

    const farPathMesh = this.pathPipeline.instancedFarPathMesh;
    if (
      this.config.useInstancedPaths &&
      farPathMesh &&
      meshId === farPathMesh.id &&
      instanceId !== undefined
    ) {
      const resolved =
        this.pathPipeline.resolveInstancedPathInstance(instanceId);
      if (!resolved || resolved.bothStationsNear) {
        // Both endpoints are near: their markers are visible, so the user
        // can pick the station directly. Skip path-mediated hover.
        return null;
      }
      return this.markerPipeline.getOrCreateClosestStationForSegment(
        resolved.data.startStationIndex,
        resolved.data.endStationIndex
      );
    }

    const nearData = this.pathPipeline.resolveNearPathMeshId(meshId);
    if (nearData) {
      return this.markerPipeline.getOrCreateClosestStationForSegment(
        nearData.startStationIndex,
        nearData.endStationIndex
      );
    }

    const segment = this.pathPipeline.findSegmentByMeshId(meshId);
    if (!segment) {
      return null;
    }

    return this.markerPipeline.getOrCreateClosestStationForSegment(
      segment.startStationIndex,
      segment.endStationIndex
    );
  }

  /**
   * Marker-only variant for `MapStationCapability`, which only reacts to
   * direct marker clicks (paths in 2D map view aren't pickable).
   */
  public findMarkerByMeshId(meshId: number): StationPoint | undefined {
    return this.markerPipeline.findByMeshId(meshId);
  }

  private addListeners(): void {
    if (!this.haveEventListeners) {
      this.threeJsManager.addPickListener(this.handlePickInternal);
      this.threeJsManager.addHoverListener(this.handleHover);
      this.threeJsManager.addAnimationFrameListener(this.updateStationPoints);
      this.haveEventListeners = true;
    }
  }

  private async removeListeners(): Promise<void> {
    if (this.haveEventListeners) {
      this.threeJsManager.removePickListener(this.handlePickInternal);
      this.threeJsManager.removeHoverListener(this.handleHover);

      await this.threeJsManager.removeAnimationFrameListener(
        this.updateStationPoints
      );

      this.haveEventListeners = false;
    }
  }

  private async removeAllVisuals(): Promise<void> {
    this.isUpdating = false;
    this.pendingUpdate = null;
    this.cancelPendingPathRebuild();

    // Clear shared metadata first so reactive consumers see the empty state
    // on the next tick; each pipeline tears down its own resources async.
    this.stationDataMap.clear();
    this.stationIndexToKey.clear();
    this.previousPositionKeys.clear();

    // Marker and path Three.js resources are independent; dispose in parallel.
    await Promise.all([
      this.markerPipeline.disposeAll(),
      this.pathPipeline.disposeAll(),
    ]);
  }

  /**
   * Last-write-wins queue for delta updates. Each `stationsInfo` carries the
   * full state, so dropping intermediates during rapid loads is safe.
   */
  private async updateVisuals(stationsInfo: StationInfo[]): Promise<void> {
    if (this.isUpdating) {
      this.pendingUpdate = stationsInfo;
      return;
    }

    this.isUpdating = true;

    try {
      await this.updateVisualsInternal(stationsInfo);

      while (this.pendingUpdate !== null) {
        const nextPositions = this.pendingUpdate;
        this.pendingUpdate = null;
        await this.updateVisualsInternal(nextPositions);
      }
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Single-pass delta: only added/removed stations are processed; the path
   * mesh rebuild is debounced so it only fires once loading settles.
   */
  private async updateVisualsInternal(
    stationsInfo: StationInfo[]
  ): Promise<void> {
    if (stationsInfo.length === 0) {
      this.cancelPendingPathRebuild();
      await this.removeAllVisuals();
      await this.removeListeners();
      return;
    }

    const currentKeys = new Set<string>();
    const newStations: Array<{ station: StationInfo; index: number }> = [];

    for (let i = 0; i < stationsInfo.length; i++) {
      const key = StationViewManager.positionKey(stationsInfo[i].position);
      currentKeys.add(key);
      if (!this.previousPositionKeys.has(key)) {
        newStations.push({ station: stationsInfo[i], index: i });
      }
    }

    const removedKeys: string[] = [];
    for (const key of this.previousPositionKeys) {
      if (!currentKeys.has(key)) {
        removedKeys.push(key);
      }
    }

    if (removedKeys.length > 0) {
      this.removeStationDataByKeys(removedKeys);
    }

    if (newStations.length > 0) {
      this.addStationDataDelta(newStations, stationsInfo);
    }

    if (newStations.length > 0 || removedKeys.length > 0) {
      this.schedulePathRebuild(stationsInfo);
    }

    this.previousPositionKeys = currentKeys;

    if (stationsInfo.length > 0 && !this.haveEventListeners) {
      this.addListeners();
    } else if (stationsInfo.length === 0 && this.haveEventListeners) {
      await this.removeListeners();
    }
  }

  /**
   * Each call resets the timer so the destroy+recreate only fires once
   * after rapid run loading settles.
   */
  private schedulePathRebuild(stationsInfo: StationInfo[]): void {
    this.cancelPendingPathRebuild();
    this.latestStationsForPathRebuild = stationsInfo;
    this.pathRebuildScheduledAt = performance.now();
    this.pathRebuildRafId = requestAnimationFrame(this.checkPathRebuildReady);
  }

  private readonly checkPathRebuildReady = (): void => {
    if (this.pathRebuildScheduledAt === null) return;

    if (
      performance.now() - this.pathRebuildScheduledAt >=
      PATH_REBUILD_DEBOUNCE_MS
    ) {
      this.pathRebuildScheduledAt = null;
      this.pathRebuildRafId = null;
      const latest = this.latestStationsForPathRebuild;
      this.latestStationsForPathRebuild = null;
      if (latest) {
        void this.updatePathSegments(latest);
      }
    } else {
      this.pathRebuildRafId = requestAnimationFrame(this.checkPathRebuildReady);
    }
  };

  private cancelPendingPathRebuild(): void {
    if (this.pathRebuildRafId !== null) {
      cancelAnimationFrame(this.pathRebuildRafId);
      this.pathRebuildRafId = null;
    }
    this.pathRebuildScheduledAt = null;
    this.latestStationsForPathRebuild = null;
  }

  private removeStationDataByKeys(removedKeys: string[]): void {
    this.markerPipeline.disposeMarkersForKeys(removedKeys);

    for (const key of removedKeys) {
      this.stationDataMap.delete(key);
    }
  }

  public static positionKey(pos: Vector3): string {
    return `${pos.x}_${pos.y}_${pos.z}`;
  }

  private addStationDataDelta(
    newStations: Array<{ station: StationInfo; index: number }>,
    allStationsInfo: StationInfo[]
  ): void {
    for (const { station: newStation, index: stationIndex } of newStations) {
      const key = StationViewManager.positionKey(newStation.position);

      let directionAngle = 0;
      let hasArrow = false;

      if (stationIndex < allStationsInfo.length - 1) {
        const nextStation = allStationsInfo[stationIndex + 1];
        if (nextStation && nextStation.runId === newStation.runId) {
          const dx = newStation.position.x - nextStation.position.x;
          const dy = newStation.position.y - nextStation.position.y;
          directionAngle = Math.atan2(dy, dx) - Math.PI;
          hasArrow = true;
        }
      }

      const stationZ = this.deps.getStationZ(newStation.position);
      this.stationDataMap.set(key, {
        key,
        stationIndex,
        originalPosition: newStation.position.clone(),
        position: new Vector3(
          newStation.position.x,
          newStation.position.y,
          stationZ
        ),
        distance: 0,
        isWithinMarkerDistance: false,
        directionAngle,
        hasArrow,
      });
    }
  }

  private buildStationIndexToKey(stationsInfo: StationInfo[]): void {
    this.stationIndexToKey.clear();
    for (let i = 0; i < stationsInfo.length; i++) {
      this.stationIndexToKey.set(
        i,
        StationViewManager.positionKey(stationsInfo[i].position)
      );
    }

    // Reorder stationIndex on existing data to match the new arrangement.
    for (let i = 0; i < stationsInfo.length; i++) {
      const key = StationViewManager.positionKey(stationsInfo[i].position);
      const data = this.stationDataMap.get(key);
      if (data) {
        data.stationIndex = i;
      }
    }

    this.markerPipeline.reindexMarkers();
  }

  /**
   * Rebuild order matters: index→key bridge first (so path lookups resolve),
   * then create new meshes, then dispose the old ones — preserves the "no
   * visual gap during rebuild" behaviour.
   */
  private async updatePathSegments(stationsInfo: StationInfo[]): Promise<void> {
    const disposeOldPaths = this.snapshotAndResetPathPipeline();

    this.buildStationIndexToKey(stationsInfo);

    // eslint-disable-next-line no-magic-numbers -- minimum 2 stations required to draw a path
    if (stationsInfo.length >= 2) {
      this.pathPipeline.createPaths(stationsInfo);

      // The debounced rebuild may run after the LOD system already moved
      // stations to "near", consuming `lodStateChanged` with no effect.
      // Force the recolour so initial state is correct.
      this.pathPipeline.updateTargetColors(this.deps.isStationMarkersVisible());
    }

    await disposeOldPaths();
  }

  /**
   * Hands out the disposal as a deferred promise so the caller can build
   * fresh path resources before the old ones tear down. The pipeline clears
   * its internal state synchronously inside `disposeAll` before the first await.
   */
  private snapshotAndResetPathPipeline(): () => Promise<void> {
    const pending = this.pathPipeline.disposeAll();
    return () => pending;
  }

  public async cleanup(): Promise<void> {
    this.markerPipeline.clearHover();

    // The clip-state tracker is self-disposing via its enclosing reactive
    // owner. Tearing it down here would permanently detach its listeners,
    // and `cleanup()` is re-entrant via `CapabilityBase.disable()`.

    // Chain cleanup so we don't interrupt an in-flight operation.
    this.operationChain = this.operationChain.then(async () => {
      await this.removeListeners();
      await this.removeAllVisuals();
    });

    await this.operationChain;
  }
}
